import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { customerApi } from "../lib/api";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { LogIn, Package, ShoppingBag, Mail, Lock, User } from "lucide-react";

const BACK_LABELS = { front_only: "Front Only", team_back: "Roster Back", name_number: "Name + Number" };

function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function SignInPanel() {
  const { loginGoogle, loginEmail, registerEmail } = useCustomerAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const tab = (m, label, tid) => (
    <button data-testid={tid} onClick={() => setMode(m)}
      className={`flex-1 py-3 text-xs uppercase tracking-widest font-bold transition-colors ${mode === m ? "text-black bg-[var(--gv-blue)]" : "text-gray-400 bg-white/5"}`}>{label}</button>
  );
  const submit = async () => {
    if (!f.email || !f.password) return toast.error("Email and password are required");
    setBusy(true);
    try {
      if (mode === "register") await registerEmail(f.name, f.email, f.password);
      else await loginEmail(f.email, f.password);
      toast.success(mode === "register" ? "Account created!" : "Welcome back!");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const input = "w-full bg-black/40 border border-white/10 px-3 py-3 pl-10 text-sm text-white focus:border-[var(--gv-blue)] outline-none";
  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="account-signin-prompt">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[var(--gv-blue)] flex items-center justify-center mx-auto mb-4"><ShoppingBag className="w-8 h-8 text-black" /></div>
        <h1 className="font-display text-4xl sm:text-5xl">MY ORDERS</h1>
        <p className="mt-2 text-gray-400 text-sm">Sign in to see your War Eagles order history. It's optional — you can always check out as a guest.</p>
      </div>
      <div className="border border-white/10 bg-[#141518]">
        <div className="flex">{tab("login", "Sign In", "auth-tab-login")}{tab("register", "Create Account", "auth-tab-register")}</div>
        <div className="p-6 space-y-3">
          {mode === "register" && (
            <div className="relative"><User className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="auth-name" placeholder="Full Name" value={f.name} onChange={set("name")} className={input} /></div>
          )}
          <div className="relative"><Mail className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="auth-email" type="email" placeholder="Email" value={f.email} onChange={set("email")} className={input} /></div>
          <div className="relative"><Lock className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="auth-password" type="password" placeholder="Password" value={f.password} onChange={set("password")}
              onKeyDown={(e) => e.key === "Enter" && submit()} className={input} /></div>
          <button data-testid="auth-submit-btn" onClick={submit} disabled={busy}
            className="w-full bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm py-3 transition-colors disabled:opacity-50">
            {busy ? "Please wait…" : mode === "register" ? "Create Account" : "Sign In"}
          </button>
          <div className="flex items-center gap-3 py-1"><div className="flex-1 h-px bg-white/10" /><span className="text-[10px] uppercase tracking-widest text-gray-500">or</span><div className="flex-1 h-px bg-white/10" /></div>
          <button data-testid="account-google-signin-btn" onClick={loginGoogle}
            className="w-full flex items-center justify-center gap-2 border border-white/20 hover:border-[var(--gv-blue-bright)] text-white font-bold uppercase tracking-widest text-sm py-3 transition-colors">
            <LogIn className="w-4 h-4" /> Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtDate(s) {
  try { return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return ""; }
}

export default function Account() {
  const { user, loading, logout } = useCustomerAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!user) return;
    customerApi.get("/customer/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-20 text-gray-400">Loading…</div>;
  if (!user) return <SignInPanel />;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="account-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--gv-blue-bright)] font-bold mb-1">My Account</p>
          <h1 className="font-display text-4xl sm:text-5xl">MY ORDERS</h1>
          <p className="mt-2 text-sm text-gray-400" data-testid="account-user-email">{user.name ? `${user.name} • ` : ""}{user.email}</p>
        </div>
        <button onClick={logout} data-testid="account-logout-btn"
          className="border border-white/20 hover:border-[var(--gv-blue-bright)] text-white font-bold uppercase tracking-widest text-xs px-6 py-3 transition-colors">
          Log Out
        </button>
      </div>

      {orders === null ? (
        <p className="text-gray-400">Loading your orders…</p>
      ) : orders.length === 0 ? (
        <div className="border border-white/10 bg-[#141518] p-10 text-center" data-testid="account-no-orders">
          <Package className="w-10 h-10 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-300 font-bold">No orders yet</p>
          <p className="text-sm text-gray-500 mt-1">When you place an order with this email, it will show up here.</p>
          <Link to="/shop/shop-all" className="inline-block mt-6 bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 transition-colors">Shop Now</Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="account-orders-list">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.order_number}`} className="border border-white/10 bg-[#141518] p-5">
              <div className="flex items-center justify-between flex-wrap gap-2 pb-3 border-b border-white/10">
                <div>
                  <p className="font-bold text-sm">Order #{o.order_number}</p>
                  <p className="text-xs text-gray-500">{fmtDate(o.created_at)} • {o.fulfillment === "shipping" ? "Shipping" : "Pickup"}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs uppercase tracking-widest font-bold px-3 py-1 bg-[var(--gv-blue)]/15 text-[var(--gv-blue-bright)]">{o.status}</span>
                  <p className="mt-1 font-bold text-[var(--gv-blue-bright)]">${Number(o.total).toFixed(2)}</p>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                {(o.items || []).map((it, i) => (
                  <p key={i} className="text-sm text-gray-300">
                    {it.quantity}× {it.product_name} — {it.garment_color} • {it.front_design_label || ""} • {BACK_LABELS[it.back_option] || ""} • {it.size}
                    {(it.player_name || it.player_number) ? <span className="text-gray-500"> ({it.player_name} #{it.player_number})</span> : null}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
